import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import {
  BugAntIcon,
  ArrowDownTrayIcon,
  FlagIcon,
  GlobeAltIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './UI/Card';
import { Button } from './UI/Button';
import { Alert, AlertDescription } from './UI/Alert';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './UI/Select';
import LoadingSpinner from './UI/LoadingSpinner';
import { aptFeedService, TrendingActor } from '../services/aptFeedService';
import { CHART_PALETTE, CHART_COLORS, SEVERITY_COLORS } from '../utils/chartColors';

const PERIOD_OPTIONS = [
  { value: 7, label: 'Últimos 7 dias' },
  { value: 30, label: 'Últimos 30 dias' },
  { value: 90, label: 'Últimos 90 dias' },
];

const PIE_COLORS = CHART_PALETTE;

const SOPHISTICATION_COLOR: Record<string, string> = SEVERITY_COLORS;

const MOTIVATION_LABEL: Record<string, string> = {
  financial: 'Financeiro',
  espionage: 'Espionagem',
  hacktivism: 'Hacktivismo',
  disruption: 'Disrupção',
  data_theft: 'Roubo de Dados',
  extortion: 'Extorsão',
  unknown: 'Desconhecido',
};

function SophisticationBadge({ value }: { value: string | null }) {
  const color = value ? SOPHISTICATION_COLOR[value] ?? CHART_COLORS.gray : CHART_COLORS.gray;
  const label = value === 'nation_state' ? 'Estado-Nação' : value ?? '—';
  return (
    <span
      style={{
        background: color,
        color: '#fff',
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {label}
    </span>
  );
}

function Progress({ value, color }: { value: number; color: string }) {
  return (
    <div className='mt-2 h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800'>
      <div
        className='h-full rounded-full transition-all duration-300'
        style={{ width: `${Math.min(Math.max(value, 0), 100)}%`, backgroundColor: color }}
      />
    </div>
  );
}

function ActorCard({ actor, onClick }: { actor: TrendingActor; onClick: () => void }) {
  const progressColor = actor.avg_confidence >= 0.7 ? CHART_COLORS.danger : actor.avg_confidence >= 0.4 ? CHART_COLORS.warning : CHART_COLORS.success;
  return (
    <div
      onClick={onClick}
      className='border border-gray-200 dark:border-gray-700 rounded-lg p-3 cursor-pointer transition-all duration-150 hover:border-primary-500 hover:ring-2 hover:ring-primary-200 dark:hover:ring-primary-800'
    >
      <div className='flex justify-between items-start mb-2'>
        <p className='font-bold text-sm text-gray-900 dark:text-white'>{actor.name}</p>
        <SophisticationBadge value={actor.sophistication} />
      </div>
      <div className='flex gap-1.5 flex-wrap mb-2'>
        {actor.origin_country && (
          <span className='inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-gray-700 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300'>
            <GlobeAltIcon className='h-3.5 w-3.5' />
            {actor.origin_country}
          </span>
        )}
        {actor.motivation && (
          <span className='rounded-full border border-warning-200 dark:border-warning-800 px-2 py-0.5 text-xs text-warning-700 dark:text-warning-300'>
            {MOTIVATION_LABEL[actor.motivation] ?? actor.motivation}
          </span>
        )}
      </div>
      <div className='flex items-center gap-1.5'>
        <ExclamationTriangleIcon className='h-4 w-4 text-orange-500 shrink-0' />
        <p className='text-xs text-gray-500 dark:text-gray-400'>
          <strong className='text-gray-700 dark:text-gray-300'>{actor.event_count}</strong> eventos · confiança média{' '}
          <strong className='text-gray-700 dark:text-gray-300'>{(actor.avg_confidence * 100).toFixed(0)}%</strong>
        </p>
      </div>
      <Progress value={actor.avg_confidence * 100} color={progressColor} />
    </div>
  );
}

const TABS = [
  { value: 'trending', label: 'Trending' },
  { value: 'ttp', label: 'TTP Heatmap' },
  { value: 'sectors', label: 'Exposição Setorial' },
  { value: 'timeline', label: 'Timeline' },
];

export const APTFeedDashboard: React.FC = () => {
  const [periodDays, setPeriodDays] = useState(30);
  const [activeTab, setActiveTab] = useState('trending');
  const [selectedActorId, setSelectedActorId] = useState<number | null>(null);

  const queryClient = useQueryClient();

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['apt-feed-metrics', periodDays],
    queryFn: () => aptFeedService.getMetrics(periodDays),
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: trending = [], isLoading: trendingLoading } = useQuery({
    queryKey: ['apt-feed-trending', periodDays],
    queryFn: () => aptFeedService.getTrending(periodDays, 15),
    refetchInterval: 5 * 60 * 1000,
  });

  const { data: ttpHeatmap = [], isLoading: ttpLoading } = useQuery({
    queryKey: ['apt-feed-ttp', periodDays],
    queryFn: () => aptFeedService.getTTPHeatmap(periodDays),
    enabled: activeTab === 'ttp',
  });

  const { data: sectorExposure = [], isLoading: sectorLoading } = useQuery({
    queryKey: ['apt-feed-sector', periodDays],
    queryFn: () => aptFeedService.getSectorExposure(periodDays),
    enabled: activeTab === 'sectors',
  });

  const { data: timeline = [], isLoading: timelineLoading } = useQuery({
    queryKey: ['apt-feed-timeline', selectedActorId, periodDays],
    queryFn: () => aptFeedService.getActorTimeline(selectedActorId!, 90),
    enabled: !!selectedActorId && activeTab === 'timeline',
  });

  const snapshotMutation = useMutation({
    mutationFn: aptFeedService.triggerSnapshot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apt-feed'] });
    },
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['apt-feed'] });
  };

  const isLoading = metricsLoading || trendingLoading;

  const trendingChartData = trending.slice(0, 10).map(a => ({
    name: a.name.length > 14 ? a.name.slice(0, 12) + '…' : a.name,
    eventos: a.event_count,
    confianca: Math.round(a.avg_confidence * 100),
  }));

  const pieData = sectorExposure.map(s => ({ name: s.sector, value: s.actor_count }));

  const periodLabel = PERIOD_OPTIONS.find(o => o.value === periodDays)?.label;
  const selectedActorName = trending.find(a => a.id === selectedActorId)?.name;

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
        <div>
          <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>APT Threat Feed</h2>
          <p className='text-gray-600 dark:text-gray-400'>Monitoramento de grupos de ameaça avançada em tempo real</p>
        </div>
        <div className='flex flex-wrap gap-2 items-center'>
          <div className='min-w-[170px]'>
            <Select value={String(periodDays)} onValueChange={v => setPeriodDays(Number(v))}>
              <SelectTrigger>
                <SelectValue placeholder='Período'>{periodLabel}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant='outline' size='sm' onClick={refreshAll} disabled={isLoading}>
            <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant='outline'
            size='sm'
            title='Gera snapshot do estado atual do feed (Analyst+)'
            onClick={() => snapshotMutation.mutate()}
            disabled={snapshotMutation.isPending}
          >
            <ArrowDownTrayIcon className='h-4 w-4 mr-1.5' />
            Snapshot
          </Button>
        </div>
      </div>

      {/* Metric cards */}
      {metrics && (
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>APTs Detectadas</CardTitle>
              <BugAntIcon className='h-4 w-4 text-gray-500' />
            </CardHeader>
            <CardContent>
              <div className='text-3xl font-bold text-gray-900 dark:text-white'>{metrics.total_actors_seen}</div>
              <p className='text-xs text-gray-500 dark:text-gray-400'>{metrics.period_days} dias</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>Eventos Vinculados</CardTitle>
              <ExclamationTriangleIcon className='h-4 w-4 text-gray-500' />
            </CardHeader>
            <CardContent>
              <div className='text-3xl font-bold text-gray-900 dark:text-white'>{metrics.total_events_linked}</div>
              <p className='text-xs text-gray-500 dark:text-gray-400'>com atribuição</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>Alta Confiança</CardTitle>
              <ShieldCheckIcon className='h-4 w-4 text-gray-500' />
            </CardHeader>
            <CardContent>
              <div className='text-3xl font-bold text-orange-600'>{metrics.high_confidence_actors}</div>
              <p className='text-xs text-gray-500 dark:text-gray-400'>confiança ≥ 70%</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <CardTitle className='text-sm font-medium'>Estado-Nação</CardTitle>
              <FlagIcon className='h-4 w-4 text-gray-500' />
            </CardHeader>
            <CardContent>
              <div className='text-3xl font-bold text-red-600'>{metrics.nation_state_actors}</div>
              <p className='text-xs text-gray-500 dark:text-gray-400'>nation-state APTs</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div>
        <div className='border-b border-gray-200 dark:border-gray-700'>
          <nav className='-mb-px flex gap-6 overflow-x-auto'>
            {TABS.map(tab => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.value
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* TRENDING TAB */}
        {activeTab === 'trending' && (
          <div className='space-y-4 mt-4'>
            <Card>
              <CardHeader>
                <CardTitle>APTs Mais Ativas</CardTitle>
                <CardDescription>Volume de eventos vinculados no período selecionado</CardDescription>
              </CardHeader>
              <CardContent>
                {trendingLoading ? (
                  <div className='flex justify-center py-8'><LoadingSpinner size='lg' /></div>
                ) : trending.length === 0 ? (
                  <Alert>
                    <AlertDescription>Nenhum evento vinculado a APTs no período. Execute o seed em /threat-actors/seed para popular os atores.</AlertDescription>
                  </Alert>
                ) : (
                  <div className='h-72'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <BarChart data={trendingChartData} layout='vertical'>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis type='number' />
                        <YAxis type='category' dataKey='name' width={110} tick={{ fontSize: 12 }} />
                        <RechartsTooltip />
                        <Bar dataKey='eventos' fill={CHART_COLORS.primary} name='Eventos' />
                        <Bar dataKey='confianca' fill={CHART_COLORS.warning} name='Confiança %' />
                        <Legend />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
              {trending.map(actor => (
                <ActorCard
                  key={actor.id}
                  actor={actor}
                  onClick={() => {
                    setSelectedActorId(actor.id);
                    setActiveTab('timeline');
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* TTP TAB */}
        {activeTab === 'ttp' && (
          <div className='mt-4'>
            <Card>
              <CardHeader>
                <CardTitle>Frequência de TTPs</CardTitle>
                <CardDescription>Técnicas MITRE ATT&amp;CK mais usadas pelos APTs ativos no período</CardDescription>
              </CardHeader>
              <CardContent>
                {ttpLoading ? (
                  <div className='flex justify-center py-8'><LoadingSpinner size='lg' /></div>
                ) : ttpHeatmap.length === 0 ? (
                  <Alert>
                    <AlertDescription>Sem dados de TTPs para o período.</AlertDescription>
                  </Alert>
                ) : (
                  <div className='h-96'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <BarChart data={ttpHeatmap.slice(0, 20)} layout='vertical'>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis type='number' />
                        <YAxis type='category' dataKey='ttp' width={90} tick={{ fontSize: 12 }} />
                        <RechartsTooltip
                          formatter={(value, name, props) => [value, `${props.payload.tactic}`]}
                        />
                        <Bar dataKey='count' fill={CHART_COLORS.danger} name='Frequência' />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* SECTOR TAB */}
        {activeTab === 'sectors' && (
          <div className='mt-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <Card>
                <CardHeader>
                  <CardTitle>Distribuição Setorial</CardTitle>
                  <CardDescription>Setores mais visados pelos APTs ativos</CardDescription>
                </CardHeader>
                <CardContent>
                  {sectorLoading ? (
                    <div className='flex justify-center py-8'><LoadingSpinner size='lg' /></div>
                  ) : pieData.length === 0 ? (
                    <Alert>
                      <AlertDescription>Sem dados de setores para o período.</AlertDescription>
                    </Alert>
                  ) : (
                    <div className='h-72'>
                      <ResponsiveContainer width='100%' height='100%'>
                        <PieChart>
                          <Pie data={pieData} dataKey='value' nameKey='name' cx='50%' cy='50%' outerRadius={100} label={(entry: any) => `${entry.name} ${((entry.percent ?? 0) * 100).toFixed(0)}%`}>
                            {pieData.map((_, idx) => (
                              <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Ranking de Setores</CardTitle>
                  <CardDescription>Por número de atores distintos</CardDescription>
                </CardHeader>
                <CardContent>
                  {sectorLoading ? (
                    <div className='flex justify-center py-8'><LoadingSpinner size='lg' /></div>
                  ) : (
                    <div className='space-y-3'>
                      {sectorExposure.map((s, i) => (
                        <div key={s.sector}>
                          <div className='flex justify-between text-sm mb-1'>
                            <span className='font-medium capitalize text-gray-700 dark:text-gray-300'>{s.sector}</span>
                            <span className='text-gray-500 dark:text-gray-400'>{s.actor_count} APT{s.actor_count > 1 ? 's' : ''} · {s.percentage}%</span>
                          </div>
                          <Progress
                            value={s.percentage}
                            color={i === 0 ? CHART_COLORS.danger : i === 1 ? CHART_COLORS.warning : CHART_COLORS.primary}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* TIMELINE TAB */}
        {activeTab === 'timeline' && (
          <div className='mt-4 space-y-4'>
            <div className='min-w-[220px] max-w-xs'>
              <Select value={selectedActorId != null ? String(selectedActorId) : ''} onValueChange={v => setSelectedActorId(Number(v))}>
                <SelectTrigger>
                  <SelectValue placeholder='Selecionar APT'>{selectedActorName ?? 'Selecionar APT'}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {trending.map(a => (
                    <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Timeline de Eventos</CardTitle>
                <CardDescription>
                  {selectedActorId
                    ? `Eventos vinculados nos últimos 90 dias — ${selectedActorName ?? ''}`
                    : 'Selecione um APT acima'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {timelineLoading ? (
                  <div className='flex justify-center py-8'><LoadingSpinner size='lg' /></div>
                ) : !selectedActorId ? (
                  <Alert>
                    <AlertDescription>Selecione um APT para ver a timeline.</AlertDescription>
                  </Alert>
                ) : timeline.length === 0 ? (
                  <Alert>
                    <AlertDescription>Sem eventos registrados para esse ator no período.</AlertDescription>
                  </Alert>
                ) : (
                  <div className='h-72'>
                    <ResponsiveContainer width='100%' height='100%'>
                      <LineChart data={timeline}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='date' tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <RechartsTooltip />
                        <Line type='monotone' dataKey='count' stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} name='Eventos' />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default APTFeedDashboard;
